
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bold,
  ChevronDown,
  Code2,
  ImagePlus,
  Italic,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Smile,
  Smartphone,
  Sparkles,
  Strikethrough,
  Table2,
  Underline,
  Undo2,
  Video,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { InsertImageDialog } from '@/components/painel/cms/InsertImageDialog';
import { InsertSmilieDialog } from '@/components/painel/cms/InsertSmilieDialog';
import { InsertPowerMentionDialog } from '@/components/painel/cms/InsertPowerMentionDialog';
import { painelPermissionsModalScrollClass } from '@/components/painel/painel-styles';
import { useRichEditorDoubleParenSmilie } from '@/hooks/useRichEditorDoubleParenSmilie';
import { useRichEditorSelection } from '@/hooks/use-rich-editor-selection';

type ChangelogRichEditorProps = {
  value: string;
  onChange: (html: string) => void;
  className?: string;
  disabled?: boolean;
  resetKey?: string;
  minHeightClass?: string;
};

function exec(cmd: string, valueArg?: string) {
  try {
    document.execCommand(cmd, false, valueArg);
  } catch {
    /* ignore */
  }
}

function TbBtn({
  label,
  children,
  onRun,
  disabled,
}: {
  label: string;
  children: React.ReactNode;
  onRun: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="h-8 min-w-8 rounded-lg px-2 text-blue-100 hover:bg-white/10"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onRun}
      disabled={disabled}
      aria-label={label}
    >
      {children}
    </Button>
  );
}

/** Editor rico para blocos de changelog — insere HTML estilizado (sem backend). */
export function ChangelogRichEditor({
  value,
  onChange,
  className,
  disabled,
  resetKey,
  minHeightClass = 'min-h-[200px]',
}: ChangelogRichEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { saveSelection, focusAndRestoreSelection, insertHtmlAtSelection } =
    useRichEditorSelection(ref);
  const [imageDlg, setImageDlg] = useState(false);
  const [smilieDlg, setSmilieDlg] = useState(false);
  const [powerDlg, setPowerDlg] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.innerHTML !== value) el.innerHTML = value || '';
  }, [resetKey, value]);

  const sync = useCallback(() => {
    const el = ref.current;
    if (el) onChange(el.innerHTML);
  }, [onChange]);

  useRichEditorDoubleParenSmilie(ref, sync, disabled);

  const runCommand = useCallback(
    (fn: () => void) => {
      focusAndRestoreSelection();
      fn();
      saveSelection();
      sync();
    },
    [focusAndRestoreSelection, saveSelection, sync]
  );

  const wrap = (html: string) => {
    insertHtmlAtSelection(html);
    sync();
  };

  const row = cn('flex flex-wrap items-center gap-0.5', disabled && 'pointer-events-none opacity-50');

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] shadow-inner shadow-black/30',
        className
      )}
    >
      <div className="border-b border-white/10 bg-white/[0.04] p-2">
        <div className={row}>
          <TbBtn label="Negrito" onRun={() => runCommand(() => exec('bold'))} disabled={disabled}>
            <Bold className="h-4 w-4" />
          </TbBtn>
          <TbBtn label="Itálico" onRun={() => runCommand(() => exec('italic'))} disabled={disabled}>
            <Italic className="h-4 w-4" />
          </TbBtn>
          <TbBtn label="Sublinhado" onRun={() => runCommand(() => exec('underline'))} disabled={disabled}>
            <Underline className="h-4 w-4" />
          </TbBtn>
          <TbBtn label="Tachado" onRun={() => runCommand(() => exec('strikeThrough'))} disabled={disabled}>
            <Strikethrough className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Lista"
            onRun={() => runCommand(() => exec('insertUnorderedList'))}
            disabled={disabled}
          >
            <List className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Lista numerada"
            onRun={() => runCommand(() => exec('insertOrderedList'))}
            disabled={disabled}
          >
            <ListOrdered className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Quote"
            onRun={() => runCommand(() => exec('formatBlock', 'blockquote'))}
            disabled={disabled}
          >
            <Quote className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Código"
            onRun={() => runCommand(() => exec('formatBlock', 'pre'))}
            disabled={disabled}
          >
            <Code2 className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Divisor"
            onRun={() =>
              wrap(
                '<hr class="my-6 border-0 border-t border-white/15 bg-transparent [height:1px] shadow-[0_0_20px_-2px_rgba(56,189,248,0.25)]" />'
              )
            }
            disabled={disabled}
          >
            <Minus className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Callout"
            onRun={() =>
              wrap(
                '<div class="changelog-callout my-4 rounded-2xl border border-sky-400/35 bg-gradient-to-br from-sky-500/15 via-white/[0.04] to-indigo-500/15 px-4 py-3 font-inter text-sm text-blue-50 shadow-[0_12px_40px_-20px_rgba(56,189,248,0.35)]"><p class="m-0 text-[11px] font-semibold uppercase tracking-wider text-sky-200/80"><span class="inline-flex items-center gap-1"><svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>Alerta da equipe</span></p><p class="mb-0 mt-2 text-[13px] leading-relaxed text-blue-50/95">Digite sua mensagem destacada aqui.</p></div>'
              )
            }
            disabled={disabled}
          >
            <Sparkles className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Spoiler"
            onRun={() =>
              wrap(
                '<details class="changelog-spoiler group my-4 overflow-hidden rounded-2xl border border-white/10 bg-black/25"><summary class="cursor-pointer list-none px-4 py-3 font-poppins text-sm font-semibold text-sky-100 outline-none ring-sky-400/0 transition hover:bg-white/[0.04] [&::-webkit-details-marker]:hidden">Spoiler <span class="ml-2 text-xs font-normal text-slate-400">(clique para expandir)</span></summary><div class="border-t border-white/10 px-4 py-3 font-inter text-sm text-blue-100/90">Conteúdo oculto até o leitor abrir.</div></details>'
              )
            }
            disabled={disabled}
          >
            <ChevronDown className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Imagem"
            onRun={() => {
              saveSelection();
              setImageDlg(true);
            }}
            disabled={disabled}
          >
            <ImagePlus className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Smilie"
            onRun={() => {
              saveSelection();
              setSmilieDlg(true);
            }}
            disabled={disabled}
          >
            <Smile className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Mencionar power"
            onRun={() => {
              saveSelection();
              setPowerDlg(true);
            }}
            disabled={disabled}
          >
            <Zap className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Vídeo"
            onRun={() =>
              wrap(
                '<div class="changelog-video my-5 aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.85)] ring-1 ring-sky-400/10"><div class="flex h-full items-center justify-center font-inter text-xs text-slate-400">URL de embed — substitua pelo iframe real</div></div>'
              )
            }
            disabled={disabled}
          >
            <Video className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Botão"
            onRun={() =>
              wrap(
                '<p class="my-3"><a href="#" class="inline-flex items-center rounded-xl border border-sky-400/40 bg-sky-500/80 px-4 py-2 font-poppins text-xs font-bold uppercase tracking-wider text-white shadow-lg shadow-sky-950/40">Call-to-action</a></p>'
              )
            }
            disabled={disabled}
          >
            <Smartphone className="h-4 w-4" />
          </TbBtn>
          <TbBtn
            label="Abas"
            onRun={() =>
              wrap(
                '<div class="changelog-tabs my-5 rounded-2xl border border-white/10 bg-white/[0.03] p-3 font-inter text-sm text-blue-50"><div class="mb-2 flex gap-2 border-b border-white/10 pb-2 text-[11px] font-semibold uppercase tracking-wider text-sky-200/80"><span class="rounded-lg bg-sky-500/20 px-2 py-1">Aba 1</span><span class="rounded-lg px-2 py-1 text-slate-400">Aba 2</span></div><p class="mb-0 mt-2 text-[13px] text-blue-100/90">Conteúdo da aba ativa.</p></div>'
              )
            }
            disabled={disabled}
          >
            <Table2 className="h-4 w-4" />
          </TbBtn>
          <TbBtn label="Desfazer" onRun={() => runCommand(() => exec('undo'))} disabled={disabled}>
            <Undo2 className="h-4 w-4" />
          </TbBtn>
          <TbBtn label="Refazer" onRun={() => runCommand(() => exec('redo'))} disabled={disabled}>
            <Redo2 className="h-4 w-4" />
          </TbBtn>
        </div>
      </div>
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        className={cn(
          minHeightClass,
          painelPermissionsModalScrollClass,
          'max-h-[520px] overflow-y-auto px-4 py-3 font-inter text-sm leading-relaxed text-blue-50',
          '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-sky-400/50 [&_blockquote]:pl-4 [&_blockquote]:text-slate-300 [&_blockquote]:italic',
          '[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:border [&_pre]:border-white/10 [&_pre]:bg-black/40 [&_pre]:p-3 [&_pre]:font-mono [&_pre]:text-[12px] [&_pre]:text-sky-100',
          '[&_img]:block [&_img]:max-w-full',
          '[&_.cms-rt-resize]:max-w-full [&_.cms-rt-resize_img]:max-w-full [&_img[data-xat-smilie]]:inline-block',
          'focus:outline-none focus:ring-2 focus:ring-sky-400/25',
          disabled && 'opacity-50'
        )}
        onInput={() => {
          saveSelection();
          sync();
        }}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onBlur={() => {
          saveSelection();
          sync();
        }}
      />
      <InsertImageDialog
        open={imageDlg}
        onOpenChange={setImageDlg}
        onInsert={wrap}
        imageVariant="changelog"
        changelogFigure
      />
      <InsertSmilieDialog open={smilieDlg} onOpenChange={setSmilieDlg} onInsert={wrap} dark />
      <InsertPowerMentionDialog open={powerDlg} onOpenChange={setPowerDlg} onInsert={wrap} dark />
    </div>
  );
}
